import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getApiUrl } from "../helpers";
import { brandConfig } from "../brandConfig";
import "./Home.css";

const API_URL = getApiUrl();

const Home: React.FC = () => {
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [navScrolled, setNavScrolled] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const revealRefs = useRef<(HTMLDivElement | null)[]>([]);

  // Nav scroll effect
  useEffect(() => {
    const handleScroll = () => setNavScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Intersection observer for reveal animations
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("visible");
          }
        });
      },
      { threshold: 0.1, rootMargin: "0px 0px -40px 0px" }
    );
    revealRefs.current.forEach((el) => {
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  const addRevealRef = (el: HTMLDivElement | null) => {
    if (el && !revealRefs.current.includes(el)) {
      revealRefs.current.push(el);
    }
  };

  const closeMobile = () => setMobileOpen(false);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitting(true);

    const form = e.currentTarget;
    const nameVal = (form.elements.namedItem("name") as HTMLInputElement).value;
    const email = (form.elements.namedItem("email") as HTMLInputElement).value;
    const phone = (form.elements.namedItem("phone") as HTMLInputElement).value;
    const eventDate = (form.elements.namedItem("event-date") as HTMLInputElement).value;
    const guests = (form.elements.namedItem("guests") as HTMLInputElement).value;
    const menu = (form.elements.namedItem("menu") as HTMLSelectElement).value;
    const dietary = (form.elements.namedItem("dietary") as HTMLInputElement).value;
    const message = (form.elements.namedItem("message") as HTMLTextAreaElement).value;

    const nameParts = nameVal.trim().split(/\s+/);
    const firstName = nameParts[0] || "";
    const lastName = nameParts.slice(1).join(" ") || "";

    const fullMessage = [
      message,
      "---",
      "Event Date: " + (eventDate || "Not specified"),
      "Number of Guests: " + (guests || "Not specified"),
      "Menu Preference: " + (menu || "Not specified"),
      "Dietary Requirements: " + (dietary || "None specified"),
    ].join("\n");

    const query =
      "mutation SubmitContactForm($input: SubmitContactFormInput!) { submitContactForm(input: $input) { id firstName lastName email } }";

    const variables = {
      input: {
        firstName,
        lastName,
        email,
        phone,
        message: fullMessage,
        formType: "contact",
        submissionUrl: window.location.href,
        notificationEmails: ["tom@tommillerservices.com", "hamishrmiller@gmail.com"],
      },
    };

    try {
      const response = await fetch(API_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-tenant-id": brandConfig.tenantId,
        },
        body: JSON.stringify({ query, variables }),
      });

      const result = await response.json();
      if (result.errors) {
        throw new Error(result.errors[0].message);
      }

      navigate("/success");
    } catch (error) {
      console.error("Submission error:", error);
      setSubmitting(false);
      alert(
        "Something went wrong. Please try again or email hamish@momentsbrisbane.com directly."
      );
    }
  };

  return (
    <>
      {/* NAV */}
      <nav className={`nav${navScrolled ? " scrolled" : ""}`}>
        <a href="#top" className="nav-logo">Moments</a>
        <ul className="nav-links">
          <li><a href="#about">About</a></li>
          <li><a href="#chef">The Chef</a></li>
          <li><a href="#menus">Menus</a></li>
          <li><a href="#services">Services</a></li>
          <li><a href="#booking">How to Book</a></li>
        </ul>
        <a href="#contact" className="nav-cta">Enquire Now</a>
        <button
          className="hamburger"
          aria-label="Menu"
          onClick={() => setMobileOpen(true)}
        >
          <span></span><span></span><span></span>
        </button>
      </nav>
      <div className={`mobile-menu${mobileOpen ? " open" : ""}`}>
        <button className="mobile-menu-close" onClick={closeMobile}>&#x2715;</button>
        <a href="#about" onClick={closeMobile}>About</a>
        <a href="#chef" onClick={closeMobile}>The Chef</a>
        <a href="#menus" onClick={closeMobile}>Menus</a>
        <a href="#services" onClick={closeMobile}>Services</a>
        <a href="#booking" onClick={closeMobile}>How to Book</a>
        <a href="#contact" onClick={closeMobile}>Enquire Now</a>
      </div>

      {/* HERO */}
      <section className="hero">
        <div className="hero-bg"><div className="hero-glow"></div></div>
        <div className="hero-content">
          <div className="hero-badge">Michelin-Trained Private Chef &middot; Brisbane</div>
          <h1 className="hero-title">Fine Dining,<br /><em>at Home</em></h1>
          <p className="hero-sub">
            Michelin-star trained Chef Hamish Miller brings the restaurant to your table.
            Bespoke menus, flawless service, unforgettable evenings.
          </p>
          <div className="hero-btns">
            <a href="#contact" className="btn-gold">Book Your Evening</a>
            <a href="#menus" className="btn-outline">View Menus</a>
          </div>
        </div>
        <div className="hero-scroll">
          <div className="hero-scroll-line"></div>
          <span>Scroll</span>
        </div>
      </section>

      {/* DIVIDER */}
      <div className="home-divider">
        <p>
          Private Dining &middot; Dinner Parties &middot; Celebrations &middot; Date Nights
          &middot; Corporate Entertaining &middot; Wine Dinners &middot; Dietary Specialists
        </p>
      </div>

      {/* ABOUT */}
      <section className="about" id="about">
        <div className="about-grid">
          <div className="about-img-wrap reveal" ref={addRevealRef}>
            <img
              src="/chef-hamish.jpg"
              alt="Chef Hamish Miller"
              className="about-img"
            />
            <div className="about-img-accent"></div>
          </div>
          <div className="reveal" ref={addRevealRef}>
            <p className="s-label">About Moments</p>
            <h2 className="s-title">
              Where Fine Dining Meets <em>Your Home</em>
            </h2>
            <p className="s-text">
              Moments Brisbane brings the precision, creativity, and elegance of
              Michelin-starred restaurants directly to your dining table. Led by Head
              Chef Hamish Miller, our team crafts bespoke multi-course experiences
              tailored to your tastes, your dietary needs, and your occasion.
            </p>
            <p className="s-text" style={{ marginTop: "1rem" }}>
              Every menu is fully customisable. Whether you're vegan, gluten-free, or
              have specific allergies — we design around you, not the other way around.
              From intimate dinners for two to celebrations for forty, we handle
              everything: preparation, presentation, service, and clean-up.
            </p>
            <div className="about-highlights">
              <div>
                <div className="about-hl-num">3</div>
                <div className="about-hl-label">Michelin Stars Trained</div>
              </div>
              <div>
                <div className="about-hl-num">7+</div>
                <div className="about-hl-label">Years in Fine Dining</div>
              </div>
              <div>
                <div className="about-hl-num">100%</div>
                <div className="about-hl-label">Bespoke Menus</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* THE CHEF */}
      <section className="chef" id="chef">
        <div className="chef-inner">
          <div className="reveal" ref={addRevealRef}>
            <p className="s-label">The Chef</p>
            <h2 className="s-title">
              Hamish <em>Miller</em>
            </h2>
            <p className="s-text">
              Australian-born, French-trained. After a decade in marketing, Hamish
              followed his true passion to France in 2015, immersing himself in the
              language and the culinary arts. He graduated from the prestigious Institut
              Paul Bocuse with a Bachelor of Culinary Arts Management, then spent four
              years honing his craft in some of Paris's most celebrated kitchens.
            </p>
            <p className="s-text" style={{ marginTop: "1rem" }}>
              Fluent in English, French, and conversational Spanish, Hamish brings an
              international perspective and a deep respect for technique, seasonality,
              and flavour. Now based in Brisbane, he leads the Moments team to deliver
              restaurant-calibre dining in the comfort of your home.
            </p>
          </div>
          <div className="chef-timeline reveal" ref={addRevealRef}>
            <div className="chef-item">
              <span className="role">Head Chef</span>
              <h4>Restaurante Caf&eacute; Jaune, Paris</h4>
              <p>
                Recreated the menu, managed the team, built customer relationships. A
                popular caf&eacute; in the heart of Paris's antique markets.
              </p>
            </div>
            <div className="chef-item">
              <div className="chef-stars">
                <span className="chef-star">&#9733;</span>
                <span className="chef-star">&#9733;</span>
              </div>
              <span className="role">Chef de Partie</span>
              <h4>Restaurant Palais Royal, Paris</h4>
              <p>
                Two Michelin star restaurant in Paris's 1st arrondissement. Responsible
                for garniture, managing a team of three.
              </p>
            </div>
            <div className="chef-item">
              <div className="chef-stars">
                <span className="chef-star">&#9733;</span>
              </div>
              <span className="role">Chef de Partie</span>
              <h4>Restaurant Automne, Paris</h4>
              <p>
                One Michelin star French restaurant with Japanese influences.
                Responsible for garniture and entremet posts.
              </p>
            </div>
            <div className="chef-item">
              <span className="role">Head Chef</span>
              <h4>Restaurant l'Insurg&eacute;, Paris</h4>
              <p>
                Bistronomic wine bar in Paris's 18th. Created menus, managed team,
                orders, inventory, and customer relationships.
              </p>
            </div>
            <div className="chef-item">
              <div className="chef-stars">
                <span className="chef-star">&#9733;</span>
              </div>
              <span className="role">Sous Chef</span>
              <h4>Restaurant Nomicos, Paris</h4>
              <p>
                One Michelin star restaurant under renowned Chef Jean-Louis Nomicos.
                Mastered every post — garde manger, entremet, fish, meat, sauces —
                before promotion to Sous Chef.
              </p>
            </div>
            <div className="chef-item">
              <div className="chef-stars">
                <span className="chef-star">&#9733;</span>
              </div>
              <span className="role">Kitchen Commis</span>
              <h4>Restaurant Saisons, Institut Paul Bocuse</h4>
              <p>
                The only restaurant within a culinary school to hold a Michelin star.
                Responsible for the meat post, menu development, and plating.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* MENUS */}
      <section className="menus" id="menus">
        <div style={{ maxWidth: 1100, margin: "0 auto" }}>
          <div className="reveal" ref={addRevealRef}>
            <p className="s-label">Our Menus</p>
            <h2 className="s-title">
              Choose Your <em>Experience</em>
            </h2>
            <p className="s-text">
              Each menu is a starting point — fully customisable to your preferences
              and dietary requirements. Allergies, vegetarian, vegan, gluten-free — we
              design around you.
            </p>
          </div>
        </div>
        <div className="menus-grid">
          <div className="menu-card reveal" ref={addRevealRef}>
            <img
              src="https://images.unsplash.com/photo-1625944230945-1b7dd3b949ab?w=600&h=440&fit=crop&crop=center"
              alt="Intimate dining"
              className="menu-card-img"
            />
            <div className="menu-card-body">
              <h3>The Intimate</h3>
              <span className="price">3-Course &middot; From $85 pp</span>
              <p>
                Perfect for date nights and small gatherings. An elegant three-course
                menu showcasing seasonal produce.
              </p>
              <ul>
                <li>Amuse-bouche</li>
                <li>Entr&eacute;e</li>
                <li>Main Course</li>
                <li>Dessert</li>
                <li>Petit fours</li>
              </ul>
            </div>
          </div>
          <div className="menu-card reveal" ref={addRevealRef}>
            <img
              src="https://images.unsplash.com/photo-1574484284002-952d92456975?w=600&h=440&fit=crop&crop=center"
              alt="Gastronomic dining"
              className="menu-card-img"
            />
            <div className="menu-card-body">
              <h3>The Gastronomic</h3>
              <span className="price">5-Course &middot; From $130 pp</span>
              <p>
                A full fine-dining journey. Five courses of refined, creative cuisine
                inspired by French technique and global flavours.
              </p>
              <ul>
                <li>Amuse-bouche</li>
                <li>Two Entr&eacute;es</li>
                <li>Fish Course</li>
                <li>Main Course</li>
                <li>Pre-dessert &amp; Dessert</li>
              </ul>
            </div>
          </div>
          <div className="menu-card reveal" ref={addRevealRef}>
            <img
              src="https://images.unsplash.com/photo-1551218808-94e220e084d2?w=600&h=440&fit=crop&crop=center"
              alt="Grand celebration"
              className="menu-card-img"
            />
            <div className="menu-card-body">
              <h3>The Celebration</h3>
              <span className="price">7-Course &middot; From $180 pp</span>
              <p>
                The ultimate culinary experience. Seven courses of show-stopping
                gastronomy with optional wine pairing.
              </p>
              <ul>
                <li>Welcome canap&eacute;s</li>
                <li>Amuse-bouche</li>
                <li>Three Entr&eacute;es</li>
                <li>Fish &amp; Main Course</li>
                <li>Cheese Course</li>
                <li>Pre-dessert &amp; Grand Dessert</li>
              </ul>
            </div>
          </div>
        </div>
        <div className="menu-note reveal" ref={addRevealRef}>
          <p>
            <strong>Every dish is customisable.</strong> Have allergies, dietary
            restrictions, or special requests? We'll work with you to design a menu
            that's perfect for everyone at your table. Vegetarian, vegan, gluten-free,
            halal, and other requirements are always welcome.
          </p>
        </div>
      </section>

      {/* SERVICES */}
      <section className="services" id="services">
        <div style={{ maxWidth: 1100, margin: "0 auto" }}>
          <div className="reveal" ref={addRevealRef}>
            <p className="s-label">Services</p>
            <h2 className="s-title">
              More Than Just <em>Dinner</em>
            </h2>
            <p className="s-text">
              Whatever the occasion, we bring the full restaurant experience to your
              chosen venue.
            </p>
          </div>
        </div>
        <div className="services-grid">
          <div className="svc-card reveal" ref={addRevealRef}>
            <div className="svc-line"></div>
            <h3>Private Chef at Home</h3>
            <p>
              Our signature experience. A chef and service team come to your home,
              prepare a multi-course meal in your kitchen, serve it, and leave
              everything spotless.
            </p>
          </div>
          <div className="svc-card reveal" ref={addRevealRef}>
            <div className="svc-line"></div>
            <h3>Dinner Parties</h3>
            <p>
              Hosting friends? We'll design a menu that flows with the evening, from
              canap&eacute;s on arrival to dessert and coffee.
            </p>
          </div>
          <div className="svc-card reveal" ref={addRevealRef}>
            <div className="svc-line"></div>
            <h3>Special Celebrations</h3>
            <p>
              Birthdays, anniversaries, engagements, milestones. We create bespoke
              menus to match the significance of your occasion.
            </p>
          </div>
          <div className="svc-card reveal" ref={addRevealRef}>
            <div className="svc-line"></div>
            <h3>Corporate Entertaining</h3>
            <p>
              Impress clients and reward your team with an elevated dining experience
              at your office or chosen venue.
            </p>
          </div>
          <div className="svc-card reveal" ref={addRevealRef}>
            <div className="svc-line"></div>
            <h3>Wine Pairing Dinners</h3>
            <p>
              Each course paired with expertly selected wines. A full sensory journey
              from start to finish.
            </p>
          </div>
          <div className="svc-card reveal" ref={addRevealRef}>
            <div className="svc-line"></div>
            <h3>Dietary Specialist Menus</h3>
            <p>
              Vegan, vegetarian, gluten-free, allergen-free — designed from the ground
              up to accommodate every dietary need without compromise on flavour.
            </p>
          </div>
        </div>
      </section>

      {/* HOW TO BOOK */}
      <section className="booking" id="booking">
        <div
          style={{ maxWidth: 1100, margin: "0 auto", textAlign: "center" }}
          className="reveal"
          ref={addRevealRef}
        >
          <p className="s-label">How to Book</p>
          <h2 className="s-title">
            From Enquiry to <em>Experience</em>
          </h2>
        </div>
        <div className="booking-steps">
          <div className="book-step reveal" ref={addRevealRef}>
            <div className="book-step-num">1</div>
            <div className="book-step-content">
              <h3>Get in Touch</h3>
              <p>
                Send us an enquiry with your date, guest count, and any preferences or
                dietary requirements. We'll respond within 24 hours.
              </p>
            </div>
          </div>
          <div className="book-step reveal" ref={addRevealRef}>
            <div className="book-step-num">2</div>
            <div className="book-step-content">
              <h3>Menu Design</h3>
              <p>
                We'll design a bespoke menu tailored to your tastes. Review, adjust,
                swap dishes — it's fully collaborative until you're thrilled.
              </p>
            </div>
          </div>
          <div className="book-step reveal" ref={addRevealRef}>
            <div className="book-step-num">3</div>
            <div className="book-step-content">
              <h3>Confirm &amp; Relax</h3>
              <p>
                Once your menu is finalised, we handle everything — sourcing the finest
                ingredients, preparation, service staff, and equipment.
              </p>
            </div>
          </div>
          <div className="book-step reveal" ref={addRevealRef}>
            <div className="book-step-num">4</div>
            <div className="book-step-content">
              <h3>Enjoy the Moment</h3>
              <p>
                On the night, sit back and savour every course. We serve, we clean, we
                disappear. All you do is enjoy.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CONTACT */}
      <section className="contact" id="contact">
        <div className="contact-grid">
          <div className="reveal" ref={addRevealRef}>
            <p className="s-label">Enquire</p>
            <h2 className="s-title">
              Let's Create Your <em>Moment</em>
            </h2>
            <p className="s-text" style={{ marginBottom: "2.5rem" }}>
              Tell us about your occasion and we'll get back to you within 24 hours
              with a tailored proposal.
            </p>
            <div className="contact-detail">
              <div className="contact-icon">&#x2709;</div>
              <div>
                <div className="contact-label">Email</div>
                <div className="contact-val">hamish@momentsbrisbane.com</div>
              </div>
            </div>
            <div className="contact-detail">
              <div className="contact-icon">&#x2706;</div>
              <div>
                <div className="contact-label">Phone</div>
                <div className="contact-val">0451 432 603</div>
              </div>
            </div>
            <div className="contact-detail">
              <div className="contact-icon">&#x25CE;</div>
              <div>
                <div className="contact-label">Location</div>
                <div className="contact-val">Brisbane, Queensland</div>
              </div>
            </div>
            <div className="contact-social">
              <a href="https://instagram.com" target="_blank" rel="noopener noreferrer">IG</a>
              <a href="https://facebook.com" target="_blank" rel="noopener noreferrer">FB</a>
            </div>
          </div>
          <div className="reveal" ref={addRevealRef}>
            <form onSubmit={handleSubmit}>
              <div className="form-row">
                <div className="form-group">
                  <label>Name *</label>
                  <input type="text" required name="name" placeholder="Your full name" />
                </div>
                <div className="form-group">
                  <label>Email *</label>
                  <input type="email" required name="email" placeholder="your@email.com" />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Phone</label>
                  <input type="tel" name="phone" placeholder="04XX XXX XXX" />
                </div>
                <div className="form-group">
                  <label>Date of Event</label>
                  <input type="date" name="event-date" />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Number of Guests *</label>
                  <input type="number" required name="guests" placeholder="e.g. 8" />
                </div>
                <div className="form-group">
                  <label>Menu Preference</label>
                  <select name="menu" defaultValue="">
                    <option value="" disabled>Select a menu...</option>
                    <option>The Intimate (3-Course)</option>
                    <option>The Gastronomic (5-Course)</option>
                    <option>The Celebration (7-Course)</option>
                    <option>Not sure yet — advise me</option>
                  </select>
                </div>
              </div>
              <div className="form-group">
                <label>Dietary Requirements</label>
                <input
                  type="text"
                  name="dietary"
                  placeholder="e.g. 2 guests vegetarian, 1 nut allergy"
                />
              </div>
              <div className="form-group">
                <label>Tell Us About Your Occasion</label>
                <textarea
                  name="message"
                  placeholder="What's the occasion? Any special requests, favourite cuisines, ingredients you love or hate..."
                ></textarea>
              </div>
              <button type="submit" className="form-submit" disabled={submitting}>
                {submitting ? "Sending..." : "Send Enquiry"}
              </button>
            </form>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="footer">
        <div className="footer-inner">
          <div className="footer-logo">Moments Brisbane</div>
          <p className="footer-text">
            &copy; 2026 Moments Brisbane. All rights reserved.
          </p>
          <ul className="footer-links">
            <li><a href="#about">About</a></li>
            <li><a href="#menus">Menus</a></li>
            <li><a href="#contact">Contact</a></li>
          </ul>
        </div>
      </footer>
    </>
  );
};

export default Home;
